import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "./ui/table";
import {
  Paperclip, Upload, Link as LinkIcon, Download, Trash2, Loader2,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const DOC_TYPE_LABELS = {
  contract_signed: "Contract semnat",
  cui_certificate: "Certificat CUI",
  identity_doc: "Document de identitate",
  consumption_proof: "Dovadă consum",
  other: "Altele",
};

const formatBytes = (bytes) => {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

const fmt = (s) => (s ? new Date(s).toLocaleString("ro-RO") : "—");

export const ContractDocuments = ({ contractId, canEdit = true }) => {
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const [uploadType, setUploadType] = useState("other");
  const [uploadingFile, setUploadingFile] = useState(null);
  const [urlForm, setUrlForm] = useState({ file_name: "", external_url: "", document_type: "other" });
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/contracts/${contractId}/documents`);
      setDocs(r.data?.documents || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [contractId]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const submitUpload = async () => {
    if (!uploadingFile) {
      toast.error("Selectează un fișier");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", uploadingFile);
      fd.append("document_type", uploadType);
      await axios.post(`${API_URL}/contracts/${contractId}/documents`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document încărcat cu succes");
      setUploadOpen(false);
      setUploadingFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const submitUrl = async () => {
    if (!urlForm.file_name.trim() || !urlForm.external_url.trim()) {
      toast.error("Numele și URL-ul sunt obligatorii");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/contracts/${contractId}/documents/register-url`, urlForm);
      toast.success("URL extern înregistrat");
      setUrlOpen(false);
      setUrlForm({ file_name: "", external_url: "", document_type: "other" });
      await fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDownload = async (doc) => {
    if (doc.storage_backend === "external_url") {
      window.open(doc.external_url, "_blank");
      return;
    }
    try {
      const r = await axios.get(
        `${API_URL}/contracts/${contractId}/documents/${doc.id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([r.data], {
        type: doc.mime_type || "application/octet-stream",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  const handleDelete = async (doc) => {
    const reason = window.prompt(`Motiv ștergere pentru "${doc.file_name}":`);
    if (!reason || reason.trim().length < 3) {
      if (reason !== null) toast.error("Motivul este obligatoriu (min 3 caractere)");
      return;
    }
    try {
      await axios.delete(`${API_URL}/contracts/${contractId}/documents/${doc.id}`, {
        data: { reason },
      });
      toast.success("Document șters (soft delete)");
      await fetchDocs();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    }
  };

  return (
    <Card data-testid="contract-documents-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Paperclip className="w-5 h-5 text-indigo-600" /> Documente contract
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            PDF/scan-uri ale contractului. Stocate local sau referențiate prin URL extern.
          </p>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setUrlOpen(true)}
              data-testid="docs-register-url-btn"
            >
              <LinkIcon className="w-4 h-4 mr-1" /> URL extern
            </Button>
            <Button
              size="sm"
              onClick={() => setUploadOpen(true)}
              data-testid="docs-upload-btn"
            >
              <Upload className="w-4 h-4 mr-1" /> Upload
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Se încarcă...
          </div>
        ) : docs.length === 0 ? (
          <div className="text-sm text-slate-500 text-center py-6" data-testid="docs-empty">
            Niciun document încărcat pentru acest contract.
          </div>
        ) : (
          <Table data-testid="docs-table">
            <TableHeader>
              <TableRow>
                <TableHead>Tip</TableHead>
                <TableHead>Nume fișier</TableHead>
                <TableHead>Mărime</TableHead>
                <TableHead>Sursă</TableHead>
                <TableHead>Backend</TableHead>
                <TableHead>Încărcat la</TableHead>
                <TableHead className="text-right">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id} data-testid={`docs-row-${d.id}`}>
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {DOC_TYPE_LABELS[d.document_type] || d.document_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium text-sm">{d.file_name}</TableCell>
                  <TableCell className="text-xs">{formatBytes(d.file_size_bytes)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{d.source}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">{d.storage_backend}</Badge>
                  </TableCell>
                  <TableCell className="text-xs">{fmt(d.uploaded_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDownload(d)}
                      data-testid={`doc-download-${d.id}`}
                    >
                      <Download className="w-3 h-3" />
                    </Button>
                    {canEdit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-rose-600"
                        onClick={() => handleDelete(d)}
                        data-testid={`doc-delete-${d.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Upload dialog */}
      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent data-testid="docs-upload-dialog">
          <DialogHeader>
            <DialogTitle>Încarcă document nou</DialogTitle>
            <DialogDescription>Fișierul se stochează local pe server cu checksum SHA-256.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tip document</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger data-testid="doc-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DOC_TYPE_LABELS).map(([v, lbl]) => (
                    <SelectItem key={v} value={v}>{lbl}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fișier</Label>
              <Input
                type="file"
                ref={fileInputRef}
                onChange={(e) => setUploadingFile(e.target.files?.[0] || null)}
                data-testid="doc-file-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>Anulează</Button>
            <Button onClick={submitUpload} disabled={saving} data-testid="doc-upload-submit">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Încarcă
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URL dialog */}
      <Dialog open={urlOpen} onOpenChange={setUrlOpen}>
        <DialogContent data-testid="docs-url-dialog">
          <DialogHeader>
            <DialogTitle>Înregistrează URL extern</DialogTitle>
            <DialogDescription>
              Pentru documente hostate extern (ex: scan-uri CRM legacy).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nume fișier</Label>
              <Input
                value={urlForm.file_name}
                onChange={(e) => setUrlForm((f) => ({ ...f, file_name: e.target.value }))}
                placeholder="ex: contract_2023.pdf"
                data-testid="doc-url-filename"
              />
            </div>
            <div>
              <Label>URL extern</Label>
              <Input
                value={urlForm.external_url}
                onChange={(e) => setUrlForm((f) => ({ ...f, external_url: e.target.value }))}
                placeholder="https://..."
                data-testid="doc-url-input"
              />
            </div>
            <div>
              <Label>Tip document</Label>
              <Select
                value={urlForm.document_type}
                onValueChange={(v) => setUrlForm((f) => ({ ...f, document_type: v }))}
              >
                <SelectTrigger data-testid="doc-url-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(DOC_TYPE_LABELS).map(([v, lbl]) => (
                    <SelectItem key={v} value={v}>{lbl}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUrlOpen(false)}>Anulează</Button>
            <Button onClick={submitUrl} disabled={saving} data-testid="doc-url-submit">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Înregistrează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ContractDocuments;
